import { useState, useRef } from 'react'
import { Group, Select, MultiSelect } from '@forwardflow/ui-v2'
import { SelectProps, Loader } from '@mantine/core'
import { CloseIcon } from '@forwardflow/icons'
import { useLeadOptions } from '../hooks'
import { useDebouncedValue } from '@forwardflow/hooks/customHooks/useDebouncedValue'
import { IFilterCriteria, TLeadType } from '../interfaces'
import { PRIMARY_FILTER_OPTIONS, ICONS_MAP } from '../constants'
import { InquiredPropertyInput } from './InquiredPropertyInput'

const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
   <Group flex="1" gap="xs">
      {ICONS_MAP[option.value as keyof typeof ICONS_MAP]}
      {option.label}
   </Group>
)

interface FilterRowProps {
   filterCriteria: IFilterCriteria
   onUpdate: (filter: IFilterCriteria) => void
   onRemove: () => void
   usedFields: Set<string>
}

export const FilterRow = ({
   filterCriteria,
   onUpdate,
   onRemove,
   usedFields,
}: FilterRowProps) => {
   const [searchValue, setSearchValue] = useState('')
   const [debouncedSearch] = useDebouncedValue(searchValue, 500)
   const { options, isLoading, loadMore, hasMore } = useLeadOptions(
      filterCriteria.field as TLeadType,
      debouncedSearch
   )
   const dropdownRef = useRef<HTMLElement | null>(null)
   const isLoadingMore = useRef(false)

   const availableFieldOptions = PRIMARY_FILTER_OPTIONS.filter(
      (option) =>
         option.value === filterCriteria.field || !usedFields.has(option.value)
   )

   const handleFieldChange = (value: string | null) => {
      if (!value) return
      setSearchValue('')
      onUpdate({
         ...filterCriteria,
         field: value as TLeadType,
         value: [],
         operator:
            value === 'inquired_property'
               ? 'CONTAINS_ANY'
               : filterCriteria.operator === 'NOT_CONTAINS_ANY'
                 ? 'NOT_CONTAINS_ANY'
                 : 'CONTAINS_ANY',
      })
   }

   const handleValuesChange = (values: string[]) => {
      onUpdate({
         ...filterCriteria,
         value: values,
      })
   }

   const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      if (hasMore && !isLoading && !isLoadingMore.current) {
         const { scrollHeight, clientHeight, scrollTop } = target
         if (scrollTop + clientHeight >= scrollHeight - 100) {
            isLoadingMore.current = true
            loadMore()
            setTimeout(() => {
               isLoadingMore.current = false
            }, 1000)
         }
      }
   }

   return (
      <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3">
         <div
            className={`grid gap-3 items-center ${
               filterCriteria.field === 'inquired_property'
                  ? 'grid-cols-[1fr_3fr_auto]'
                  : 'grid-cols-[1fr_auto_3fr_auto]'
            }`}
         >
            <Select
               key={`selectedField-${filterCriteria.field}`}
               leftSection={
                  ICONS_MAP[filterCriteria.field as keyof typeof ICONS_MAP]
               }
               data={availableFieldOptions}
               renderOption={renderSelectOption}
               value={filterCriteria.field}
               onChange={handleFieldChange}
               placeholder="Select a field"
            />
            {filterCriteria.field !== 'inquired_property' && (
               <Select
                  data={[
                     { value: 'CONTAINS_ANY', label: 'Include' },
                     { value: 'NOT_CONTAINS_ANY', label: 'Exclude' },
                  ]}
                  value={filterCriteria.operator === 'NOT_CONTAINS_ANY' ? 'NOT_CONTAINS_ANY' : 'CONTAINS_ANY'}
                  onChange={(value) => value && onUpdate({
                     ...filterCriteria,
                     operator: value as 'CONTAINS_ANY' | 'NOT_CONTAINS_ANY',
                  })}
                  w={120}
               />
            )}
            {filterCriteria.field === 'inquired_property' ? (
               <InquiredPropertyInput
                  value={filterCriteria.value.map(String)}
                  onChange={handleValuesChange}
                  placeholder="Search property addresses"
               />
            ) : (
               <MultiSelect
                  searchable
                  nothingFoundMessage={isLoading ? 'Loading...' : "No results found"}
                  key={`selectedValues-${filterCriteria.field}`}
                  searchValue={searchValue}
                  onSearchChange={setSearchValue}
                  data={options}
                  value={filterCriteria.value.map(String)}
                  onChange={handleValuesChange}
                  placeholder="Select values"
                  rightSection={isLoading ? <Loader size="xs" /> : undefined}
                  onDropdownOpen={() => {
                     setTimeout(() => {
                        const dropdown = document.querySelector(
                           '.mantine-MultiSelect-dropdown .mantine-ScrollArea-viewport'
                        ) as HTMLElement
                        if (dropdown) {
                           dropdown.addEventListener('scroll', handleScroll)
                           dropdownRef.current = dropdown
                        }
                     }, 0)
                  }}
                  onDropdownClose={() => {
                     if (dropdownRef.current) {
                        dropdownRef.current.removeEventListener(
                           'scroll',
                           handleScroll
                        )
                        dropdownRef.current = null
                     }
                  }}
               />
            )}
            <button
               onClick={onRemove}
               className="w-7 h-7 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.08] transition-colors"
            >
               <CloseIcon width={14} height={14} />
            </button>
         </div>
      </div>
   )
}
